import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import { IMAGES } from "@/lib/data";
import PageHead from "@/components/site/PageHead";
import { Reveal, Eyebrow } from "@/components/site/Motion";

const TIERS = [
  { model: "Technical review", involves: "Your subject-matter experts review the crosswalk and measure set and return written comment.", commitment: "2–4 hours of expert time", step: "An information request" },
  { model: "Observer status", involves: "Your agency attends exercises and receives all artifacts and the evaluation, without participating in training.", commitment: "Attendance only", step: "Letter of interest" },
  { model: "Founding pilot partner", involves: "Full participation: training cohorts, joint exercises, scored observation, after-action cycles.", commitment: "Named lead + staff time over 18 months", step: "Information request, then MOU" },
  { model: "Research or evaluation partner", involves: "You hold the evaluation design and analysis, independent of the program.", commitment: "Faculty time; IRB where applicable", step: "Protocol discussion" },
];

const CONTRIBUTIONS = [
  { title: "Law enforcement", items: ["Operational review", "Rescue task force integration", "Command and zoning alignment", "Scenario participation", "Policy crosswalk", "Pilot site participation"] },
  { title: "Fire/Rescue and EMS", items: ["Clinical-operational integration", "Skills testing", "Warm-zone response", "Transport coordination", "Data and quality review", "Instructor participation"] },
  { title: "Hospitals and trauma centers", items: ["Handoff requirements", "Trauma-alert workflows", "Receiving-facility integration", "Medical oversight", "Clinical review", "Outcome-research pathway"] },
  { title: "Universities", items: ["Independent evaluation", "Implementation science", "Research design", "Statistical analysis", "Simulation expertise", "Publication and dissemination"] },
  { title: "Simulation centers and academies", items: ["Facilities", "Scenario execution", "Evaluator training", "Video-assisted review", "Instructor development", "Fidelity testing"] },
  { title: "Emergency management", items: ["NIMS/ICS alignment", "Multi-agency planning", "Exercise governance", "Mutual-aid integration", "Corrective-action management", "Regional replication"] },
];

const QA = [
  ["Who approves the clinical content at our site, and can they veto it?", "Your medical director. Yes."],
  ["What data leaves our building?", "Only what a signed data-use agreement permits; the default is de-identified aggregate."],
  ["What happens if we withdraw?", "You keep your artifacts; the pilot continues with the remaining sites; no penalty."],
  ["Who publishes, and can we review first?", "The evaluator publishes; partners review before release."],
  ["What if the results are negative?", "They are published. That is the commitment."],
];

export default function Partnerships() {
  return (
    <main data-testid="partnerships-page">
      <PageHead
        chapter="05"
        eyebrow="Partnership Opportunities"
        titleLines={["Four ways to engage,", "ordered by commitment."]}
        lede="Most institutions should begin at the first tier and decide later whether to move further."
        image={IMAGES.hospitalHandoff}
      />

      {/* TIERS */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10" data-testid="tiers-section">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-px bg-line border border-line">
          {TIERS.map((t, i) => (
            <Reveal key={t.model} delay={i * 0.08} className="bg-ink">
              <div data-testid={`tier-card-${i}`} className="group flex flex-col p-8 h-full hover:bg-surface transition-colors duration-300">
                <span className="font-mono text-4xl font-bold text-outline group-hover:text-signal transition-colors duration-300">0{i + 1}</span>
                <h3 className="mt-5 font-display font-bold text-xl text-white tracking-tight">{t.model}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{t.involves}</p>
                <div className="mt-6 pt-5 border-t border-line space-y-3">
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist">Commitment</p>
                    <p className="text-sm text-slate-300 mt-1">{t.commitment}</p>
                  </div>
                  <div>
                    <p className="font-mono text-[9px] uppercase tracking-[0.2em] text-mist">First step</p>
                    <p className="text-sm text-slate-300 mt-1">{t.step}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CONTRIBUTIONS */}
      <section className="border-y border-line bg-surface py-24" data-testid="contributions-section">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <Reveal>
            <Eyebrow>By institution type</Eyebrow>
            <h2 className="mt-5 font-display font-extrabold text-white tracking-tight leading-tight" style={{ fontSize: "clamp(1.75rem, 4vw, 3rem)" }}>
              Possible contributions.
            </h2>
          </Reveal>
          <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-px bg-line border border-line">
            {CONTRIBUTIONS.map((c, i) => (
              <Reveal key={c.title} delay={i * 0.05} className="bg-ink">
                <div data-testid={`contribution-card-${i}`} className="p-8 h-full flex flex-col hover:bg-surface/60 transition-colors">
                  <h3 className="font-display font-bold text-xl text-white tracking-tight">{c.title}</h3>
                  <ul className="mt-5 space-y-2 flex-1">
                    {c.items.map((item) => (
                      <li key={item} className="flex gap-3 text-sm text-slate-400">
                        <span className="text-signal font-mono text-xs mt-0.5">→</span> {item}
                      </li>
                    ))}
                  </ul>
                  <Link to="/contact" className="mt-6 inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-300 hover:text-signal transition-colors">
                    Discuss a potential role <ArrowRight size={12} />
                  </Link>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* EXPRESSION OF INTEREST + QA */}
      <section className="py-24 max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <Eyebrow>Expression of interest</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
            What a useful expression of interest contains.
          </h2>
          <p className="mt-6 text-sm text-slate-400 leading-relaxed">
            A letter that helps is specific. At minimum it should identify: the signer's title and authority; the organization's current active-threat or interagency readiness problem, stated with operational specificity; which T-EMRP product the organization reviewed; how the proposed contribution complements existing TECC/TCCC, EMS, NIMS, or local programs; the contemplated role, participants, facilities, data, and medical direction; any resources or in-kind support anticipated; the concrete next step; and any approvals, procurement, legal, or funding conditions that remain open.
          </p>
          <div className="mt-8 border-l-2 border-signal bg-surface p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-signal mb-3">Authenticity over adjectives</p>
            <p className="text-sm text-slate-300 leading-relaxed">
              A cautious letter describing a real review, a scheduled meeting, a specific role, and an unresolved approval condition is worth more than a sweeping endorsement that identifies no planned action.
            </p>
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <Eyebrow>Due diligence</Eyebrow>
          <h2 className="mt-5 font-display font-bold text-2xl md:text-3xl text-white tracking-tight leading-tight">
            Questions a prospective partner should ask.
          </h2>
          <div className="mt-8 border border-line divide-y divide-line">
            {QA.map(([q, a], i) => (
              <div key={i} data-testid={`partner-qa-${i}`} className="p-6 hover:bg-surface transition-colors">
                <p className="font-display font-bold text-white tracking-tight">{q}</p>
                <p className="mt-2 text-sm text-signal font-mono">{a}</p>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/contact" data-testid="partnerships-cta-contact" className="inline-flex items-center gap-3 bg-signal hover:bg-signal-hover text-white font-mono text-xs uppercase tracking-[0.15em] px-6 py-4 transition-colors">
              Request More Information <ArrowUpRight size={15} />
            </Link>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
